import config from 'app/config'
import makeSiteGraphqlRequest from 'app/lib/graphql-request'
import { useStores } from 'app/store'
import { UserStore } from 'app/store/user-store'
import { useQuery as useQueryBase, UseQueryOptions } from 'react-query'
import { logError } from 'app/lib/log-error'

export async function makeQuery<T>(
  query: string,
  variables: any,
  formatFn?: (data: any) => T,
  userStore?: UserStore,
  jwtToken?: string
) {
  try {
    return await makeSiteGraphqlRequest(config, query, variables, {
      userStore,
      formatFn,
      jwtToken,
    })
  } catch (e) {
    console.error(e)
    logError(e, query, variables)
  }
}

interface UseQueryOptionsWithToken<T> extends UseQueryOptions<T> {
  jwtToken?: string
  skipAuth?: boolean
  queryKeySuffix?: string
}

export default function useQuery<T>(
  query: string,
  variables?: any,
  formatFn?: (data: any) => T,
  options?: UseQueryOptionsWithToken<T>
) {
  const { userStore } = useStores()
  const skipAuth = options?.skipAuth === true

  async function fetchData() {
    if (!skipAuth && !userStore && !options?.jwtToken) return

    return makeQuery(
      query,
      variables,
      formatFn,
      skipAuth ? undefined : userStore,
      options?.jwtToken
    )
  }

  const {
    enabled: _enabled,
    skipAuth: _skipAuth,
    queryKeySuffix,
    ...rest
  } = options || {}
  const enabled = options?.hasOwnProperty('enabled') ? options.enabled : true
  const queryKey = queryKeySuffix
    ? ['query', query, variables, queryKeySuffix]
    : ['query', query, variables]

  const { data, isLoading, error, refetch, isRefetching } = useQueryBase(
    queryKey,
    fetchData,
    {
      enabled: (skipAuth || !!userStore) && enabled,
      ...rest,
    }
  )

  return {
    data,
    loading: isLoading,
    error: error as Error,
    refetching: isRefetching,
    fetchData: refetch,
  }
}
